import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Form, Formik, Field } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { Marker } from '@react-google-maps/api';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Layout from 'components/Layout';
import PolygonItem from 'components/Map/Polygon';
import MapComponent from 'components/Map/Map';
import { useHistory, useParams } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';

import { locationActions } from 'state/modules/locations';

import { TextField, PlacesField, MaterialSelectField } from 'components/FormFields';

import { getCity, getState, getCountry, getPostalCode, convertToLatLong } from 'lib/gmaps';

import { states as stateslist, timeZones as timeZonesList, countries } from 'constants';

import { zip_by_country } from 'utils';
import { integrations } from 'config';

const MAP_URL = integrations.googleMapsUrl;
const DEFAULT_COORDS = {
  lat: 39.828272,
  lng: -98.579469,
};
// const DEFAULT_COORDS = {
//   lat: 53.5426368,
//   lng: -113.4870375
// };
const DEFAULT_MAPS_OPTIONS = {
  scrollwheel: false,
};

const formHeaderLabelSx = {
  width: '100%',
  marginBottom: 0,
  fontSize: 16,
};

/**
 * Remove the trailing closing point of a polygon ring when it duplicates the
 * first point. Google maps polygons are implicitly closed and the backend
 * re-appends the closing point on save, so keeping it here would accumulate
 * duplicate vertices on every edit/save cycle.
 * @param {Array<{lat:number, lng:number}>} points
 */
export const stripClosingPoint = (points) => {
  if (!Array.isArray(points) || points.length < 2) return points;
  const first = points[0];
  const last = points[points.length - 1];
  if (first && last && first.lat === last.lat && first.lng === last.lng) {
    return points.slice(0, -1);
  }
  return points;
};

const Locations = () => {
  const formRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, locations, fetchedLocation } = useSelector((state) => state.locations);
  const locationData = useSelector((state) => state.locations?.location);
  const errors = useSelector((state) => state.locations?.errors?.location);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );

  const [location, setLocation] = useState({});
  const [center, setCenter] = useState(DEFAULT_COORDS);
  const [zone, setZone] = useState({});
  // Holds the latest polygon path while the user edits it. Kept in a ref (not
  // state) on purpose: updating state on every vertex change re-applies the
  // `path` prop, which makes @react-google-maps/api reset the polygon's MVCArray
  // mid-edit and snaps the shape back to the initial rectangle. See onZoneChange.
  const editedZoneRef = useRef(null);
  // Bumped on deliberate polygon resets (map click, address change, load) to
  // remount the editable polygon so its edit listeners reattach to the new path.
  // It is NOT bumped on vertex edits, so editing never resets mid-gesture.
  const [zoneVersion, setZoneVersion] = useState(0);
  const [mapRef, setMapRef] = useState({});
  const [states, setStates] = useState([]);
  const [timeZones, setTimeZones] = useState([]);

  const { id } = useParams();

  const placeInput = React.createRef();

  const formSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too Short!').required(i18n.t('common.required_field')),
    zipcode: zip_by_country('US', 'CA', 'MX', 'AU', 'NZ').required(i18n.t('common.required_field')),
    time_zone: Yup.string().required(i18n.t('common.required_field')),
    state: Yup.string().nullable(),
    city: Yup.string().required(i18n.t('common.required_field')),
    place: Yup.lazy((value) => {
      switch (typeof value) {
        case 'object':
          return Yup.object().required(i18n.t('common.required_field'));
        case 'string':
          return Yup.string().required(i18n.t('common.required_field'));
        default:
          return Yup.string();
      }
    }),
  });

  const initialValues = {
    city: locationData.city || '',
    name: locationData.name || '',
    state: locationData.state || '',
    place: locationData.address || '',
    zipcode: locationData.zipcode || '',
    country: locationData.country || '',
    time_zone: locationData.time_zone || '',
  };

  const loadLocation = useCallback(
    (data) => {
      // The backend returns a closed ring (last point duplicates the first).
      // Strip it before editing: google maps closes the polygon on its own, and
      // sending the closing point back makes the backend append yet another one,
      // accumulating duplicate vertices on every save.
      const openZone = stripClosingPoint(data.zone);
      setZone((zone) => ({
        ...zone,
        zone: openZone,
        lat: data.center.lat,
        lng: data.center.lng,
        address: data.address,
      }));
      editedZoneRef.current = openZone;
      setZoneVersion((v) => v + 1);

      setLocation(data.center);

      setAddress(data);

      const bounds = getBounds(data);
      if (bounds && bounds.extend) {
        bounds.extend(data.center);
      }
      if (bounds && mapRef && mapRef.fitBounds) {
        mapRef.fitBounds(bounds);
      }
    },
    [mapRef]
  );

  useEffect(() => {
    dispatch(locationActions.fetchLocations());
    if (id) {
      dispatch(locationActions.fetchLocationsById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (fetchedLocation) {
      dispatch(locationActions.resetLocationState());
      history.push(buildUrl(`/locations/${id || locationData?.id || ''}`));
    }
  }, [fetchedLocation, history, id, locationData, dispatch]);

  useEffect(() => {
    if (locationData && locationData.id) {
      loadLocation(locationData);
    }
  }, [locationData, loadLocation]);

  /**
   * Handle searchbox changes
   * @param {*} places Google maps places array
   */
  const onPlacesChanged = (place) => {
    if (!place || !Object.keys(place).length) return;

    const country = getCountry(place, 'short_name');

    const data = setAddress({
      address: place['name'],
      city: getCity(place) || '',
      state: country === 'PR' ? 'PR' : country !== 'MX' ? getState(place) || null : null,
      zipcode: getPostalCode(place) || '',
      country: country === 'PR' ? 'US' : country,
    });

    // fill form
    Object.keys(data).map((key) => {
      return formRef.current.setFieldValue(key, data[key]);
    });

    const geometry = place['geometry']['location'];
    const coords = {
      lat: geometry.lat(),
      lng: geometry.lng(),
    };

    setZone({
      ...zone,
      lat: coords.lat,
      lng: coords.lng,
      zone: null,
    });
    editedZoneRef.current = null;
    setZoneVersion((v) => v + 1);

    setLocation(coords);
    setCenter(coords);

    // Set bounds and center map to desired location
    const bounds = getBounds(place);
    if (bounds && bounds.extend) {
      bounds.extend(coords);
    }
    if (bounds && mapRef && mapRef.fitBounds) {
      mapRef.fitBounds(bounds);
    }
  };

  const changeState = (e) => {
    if (e?.target?.name === 'country') {
      const country = e?.target?.value?.toLowerCase();
      // Autocomplete could trigger an event with a non valid country
      if (formRef?.current) {
        formRef.current.setFieldValue('state', '');
        formRef.current.setFieldValue('time_zone', '');
      }
      setStates(stateslist[country || 'us'] || stateslist['us']);
      setTimeZones(timeZonesList[country || 'us'] || timeZonesList['us']);
    }
  };

  const setAddress = (data) => {
    // get states
    const stateOptions = stateslist[data?.country?.toLowerCase() || 'us'] || stateslist.us;
    // get timezones
    const timeZoneOptions = timeZonesList[data?.country?.toLowerCase() || 'us'] || timeZonesList.us;

    const stateCode = data.state
      ? stateOptions.find((_state) => _state.label.toLowerCase() === data.state?.toLowerCase())
      : '';

    setTimeZones(timeZoneOptions);
    setStates(stateOptions);

    // set state
    if (stateCode && stateCode.value) data.state = stateCode.value;

    return data;
  };

  const getDefaultStates = (country) => {
    if (country) {
      return stateslist[country?.toLowerCase() || 'us'] || stateslist.us;
    }
    return states;
  };

  const getDefaultTimezones = (country) => {
    if (country) {
      return timeZonesList[country?.toLowerCase() || 'us'] || timeZonesList.us;
    }
    return timeZones;
  };

  /**
   * Get new bounds to be used to center the map
   * @param {*} zones
   */
  const getBounds = (zones) => {
    let bounds = {};
    if (window?.google?.maps) {
      bounds = new window.google.maps.LatLngBounds();

      if (zones) {
        if (Array.isArray(zones)) {
          zones.forEach((zone) => {
            zone.zone.forEach((coords) =>
              coords.lat && coords.lng
                ? bounds.extend(coords)
                : bounds.extend({
                    lat: coords[0],
                    lng: coords[1],
                  })
            );
          });
        } else {
          //A single zone called zones
          zones.zone &&
            zones.zone.forEach((coords) =>
              coords.lat && coords.lng
                ? bounds.extend(coords)
                : bounds.extend({
                    lat: coords[0],
                    lng: coords[1],
                  })
            );
        }
      }
    }

    return bounds;
  };

  /**
   * Create a polygon when map is clicked
   * @param {*} latLng  Google Maps Latitude Longitude Object
   */
  const createPolygon = (latLng) => {
    const currentMapBounds = mapRef.getBounds();
    let sizeLat =
      (currentMapBounds.getNorthEast().lat() - currentMapBounds.getSouthWest().lat()) * 0.25;
    let sizeLng =
      (currentMapBounds.getNorthEast().lng() - currentMapBounds.getSouthWest().lng()) * 0.25;

    return [
      {
        lat: latLng.lat() + sizeLat,
        lng: latLng.lng() - sizeLng,
      },
      {
        lat: latLng.lat() + sizeLat,
        lng: latLng.lng() + sizeLng,
      },
      {
        lat: latLng.lat() - sizeLat,
        lng: latLng.lng() + sizeLng,
      },
      {
        lat: latLng.lat() - sizeLat,
        lng: latLng.lng() - sizeLng,
      },
    ];
  };

  /**
   * Handle map clicked event
   * @param {*} e  event
   */
  const onMapClick = (e) => {
    const coords = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    if (!zone || zone.zone === null) {
      const polygon = createPolygon(e.latLng);
      setZone({
        ...zone,
        zone: polygon,
      });
      editedZoneRef.current = polygon;
      setZoneVersion((v) => v + 1);

      setCenter(coords);
    }
  };

  /**
   * Handle polygon changes.
   *
   * The new path is stored in a ref instead of state on purpose. Calling setZone
   * here would re-render and re-apply the `path` prop, which makes
   * @react-google-maps/api replace the polygon's MVCArray mid-edit and reset the
   * shape back to the initial rectangle. The latest path is read from the ref on
   * submit, while google maps owns the live visual editing.
   * @param {*} newPath array of paths
   */
  const onZoneChange = (newPath) => {
    editedZoneRef.current = newPath;
  };

  const submit = (values, { setSubmitting }) => {
    const data = { ...values };
    data.center = {
      lat: zone.lat,
      lng: zone.lng,
    };
    // Read the live edited path from the ref (see onZoneChange) and drop any
    // closing point so the backend doesn't accumulate duplicate vertices.
    data.vertices = stripClosingPoint(editedZoneRef.current || zone?.zone);
    if (id) dispatch(locationActions.updateLocation(id, data));
    else dispatch(locationActions.createLocation(data));
    setSubmitting(false);
  };

  return (
    <Layout title="Locations">
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid size={12}>
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={{ marginBottom: 20 }}
          >
            <Grid>
              <Typography variant="h6" gutterBottom>
                <Box fontWeight="fontWeightBold" fontSize={24} my={1}>
                  {id ? i18n.t('location.edit') : i18n.t('location.create')}{' '}
                  {i18n.t('location.location')}
                </Box>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Paper
          elevation={3}
          sx={{
            boxShadow: '0px 3px 6px #0000001A',
            display: 'flex',
            flexWrap: 'nowrap',
            width: '100%',
            margin: '0 auto',
            padding: 0,
            '& > *': {
              m: 1,
              width: '100%',
              height: '100%',
            },
          }}
        >
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              padding: 0,
              margin: 0,
            }}
          >
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ height: '100%', minWidth: { xs: '100%', md: 'auto' } }}>
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'common.white',
                    padding: '14px 33px 11px',
                    fontSize: '16px',
                    width: '100%',
                    flexWrap: 'wrap',
                    minHeight: 66,
                  }}
                >
                  <Typography gutterBottom variant="h6" component="span" sx={formHeaderLabelSx}>
                    <Box fontWeight="fontWeightBold" lineHeight={1}>
                      {id ? i18n.t('location.edit') : i18n.t('location.create')}{' '}
                      {i18n.t('location.location')}
                    </Box>
                  </Typography>

                  <Typography
                    gutterBottom
                    component="small"
                    variant="subtitle1"
                    sx={formHeaderLabelSx}
                  >
                    <Box fontSize={14} lineHeight={1} mt={1}>
                      {i18n.t('location.heading_subtitle')}
                    </Box>
                  </Typography>
                </Box>

                <Formik
                  initialValues={initialValues}
                  validationSchema={formSchema}
                  enableReinitialize={true}
                  innerRef={formRef}
                  onSubmit={submit}
                >
                  {({ handleSubmit, isSubmitting, values }) => (
                    <Form
                      noValidate
                      onChange={changeState}
                      onSubmit={handleSubmit}
                      style={{ minWidth: '100%', padding: '30px 25px' }}
                    >
                      {sharingType === 'FFB' ? (
                        <Box
                          component="span"
                          sx={{
                            color: 'warning.main',
                            textAlign: 'justify',
                            display: 'block',
                            marginBottom: '1rem',
                          }}
                        >
                          <WarningIcon
                            sx={{
                              fontSize: '1rem',
                              verticalAlign: 'text-bottom',
                              marginRight: '0.5rem',
                            }}
                          />
                          {i18n.t('locations.fleet_disclaimer')}
                        </Box>
                      ) : null}
                      <Field
                        type="text"
                        name="name"
                        component={TextField}
                        asyncErrors={errors?.name}
                        label={i18n.t('location.name')}
                        inputProps={{ autoComplete: 'nope' }}
                      />
                      <Box mb={2} mt={1}>
                        <Field
                          name="place"
                          mapRef={placeInput}
                          label={i18n.t('location.address')}
                          component={PlacesField}
                          onChange={onPlacesChanged}
                          inputProps={{ autoComplete: 'nope' }}
                        />
                      </Box>
                      <Grid container columnSpacing={3} rowSpacing={2.5}>
                        <Grid size={6}>
                          <Field
                            name="country"
                            options={countries}
                            handleChange={changeState}
                            asyncErrors={errors?.country}
                            component={MaterialSelectField}
                            placeholder={i18n.t('location.country')}
                          />
                        </Grid>
                        <Grid size={6}>
                          <Field
                            name="state"
                            options={getDefaultStates(values?.country)}
                            asyncErrors={errors?.state}
                            readOnly={states.length === 0}
                            component={MaterialSelectField}
                            placeholder={i18n.t('location.state')}
                          />
                        </Grid>
                        <Grid size={6}>
                          <Field
                            type="text"
                            name="city"
                            margin="none"
                            component={TextField}
                            asyncErrors={errors?.city}
                            label={i18n.t('location.city')}
                            inputProps={{ autoComplete: 'nope' }}
                          />
                        </Grid>
                        <Grid size={6}>
                          <Field
                            type="text"
                            margin="none"
                            name="zipcode"
                            component={TextField}
                            asyncErrors={errors?.zipcode}
                            inputProps={{ autoComplete: 'nope' }}
                            label={i18n.t('location.zip_code')}
                          />
                        </Grid>
                        <Grid size={6}>
                          <Field
                            name="time_zone"
                            options={getDefaultTimezones(values?.country)}
                            component={MaterialSelectField}
                            asyncErrors={errors?.time_zone}
                            readOnly={timeZones.length === 0}
                            placeholder={i18n.t('location.time_zone')}
                          />
                        </Grid>
                      </Grid>

                      <Box display="flex" alignItems="start" mt={10} justifyContent="flex-end">
                        <Button
                          color="primary"
                          sx={{ marginRight: 2 }}
                          style={{ textTransform: 'capitalize' }}
                          onClick={() => {
                            dispatch(locationActions.resetLocationState());
                            history.push(buildUrl('/locations'));
                          }}
                        >
                          {i18n.t('common.cancel')}
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          variant="contained"
                          data-cy="create-location"
                          style={{ textTransform: 'capitalize' }}
                          disabled={isSubmitting || !zone?.zone || loading}
                        >
                          {i18n.t('location.save')}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </Box>
            </Grid>

            <Divider orientation="vertical" flexItem />
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ height: '100%', width: '100%', padding: '15px' }}>
                <MapComponent
                  isMarkerShown={false}
                  googleMapURL={MAP_URL}
                  loadingElement={<div style={{ height: `100%`, width: `100%` }} />}
                  containerElement={<div style={{ height: `600px`, width: `100%` }} />}
                  mapElement={<div style={{ height: `100%`, width: `100%` }} />}
                  onClick={onMapClick}
                  center={locationData?.center || center}
                  zoom={12}
                  draggable={false}
                  defaultOptions={DEFAULT_MAPS_OPTIONS}
                  setMapRef={(ref) => setMapRef(ref)}
                >
                  {location && location.lat != null && location.lng != null && (
                    <Marker
                      position={{
                        lat: location?.lat,
                        lng: location?.lng,
                      }}
                    />
                  )}
                  {locations &&
                    locations.map((zone) => (
                      <PolygonItem
                        key={zone.id}
                        path={convertToLatLong(zone.zone.slice(0, -1))}
                        editable={false}
                        options={{
                          strokeWeight: 1,
                          fillOpacity: 0.1,
                        }}
                      />
                    ))}

                  {zone && zone.zone && (
                    <PolygonItem
                      key={zoneVersion}
                      path={zone.zone}
                      editable={true}
                      options={{
                        fillColor: '#007bff',
                        strokeWeight: 4,
                        strokeColor: '#007bff',
                        fillOpacity: 0.2,
                      }}
                      onChange={onZoneChange}
                    />
                  )}
                </MapComponent>
                {/* required reference to get the detail of a place */}
                <div id="map-element" ref={placeInput} />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Layout>
  );
};

export default Locations;
